import { createCollectionReducer } from 'ext/lib/collections';
import { TYPE } from './actions';
import type {
  EntitlementsState,
  Entitlement,
  EntitlementConfig,
} from './types';

export default createCollectionReducer(TYPE);

export const selectEntitlementsByName = <
  T extends { entitlements: EntitlementsState },
>(
  state: T,
  name: string
): Entitlement | null => {
  return state?.entitlements[name] ?? null;
};

/**
 * Checks if a specific entitlement is enabled based on the provided configuration.
 *
 * @param state - The current application state, which contains entitlement information.
 * @param name - The name of the entitlement to check.
 * @param config - The configuration object used to determine the type of entitlement. Not required in case it is not boolean type
 *
 * @returns Returns `true` if the entitlement is enabled, otherwise `false`.
 *
 * @example
 * // For a boolean entitlement type
 * const isEnabled = isEntitlementEnabled(state, 'entitlement_name', { type: 'boolean' });
 * console.log(isEnabled); // true or false based on allowed_units
 *
 * @example
 * // For a non-boolean entitlement type
 * const isEnabled = isEntitlementEnabled(state, 'another_entitlement_name');
 * console.log(isEnabled); // true if used_units < allowed_units, otherwise false
 */
export const isEntitlementEnabled = <
  T extends { entitlements: EntitlementsState },
>(
  state: T,
  name: string,
  config?: EntitlementConfig
): boolean => {
  const entitlement = selectEntitlementsByName(state, name);

  if (!entitlement) return false;

  return config?.type === 'boolean'
    ? entitlement?.allowed_units > 0
    : entitlement?.used_units < entitlement?.allowed_units;
};
